import { useMemo, useState } from 'react';

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

export interface SunPos {
  elevation: number; // degrees above horizon
  azimuth: number; // degrees from north, clockwise
  shadowRatio: number; // shadow length / object height
  shadowDir: number; // direction the shadow points (azimuth + 180)
  solarTime: number; // local solar time in hours
}

/** NOAA-Approximation der Sonnenposition für einen Zeitpunkt (UTC) und Ort. */
export function sunPosition(date: Date, lat: number, lng: number): SunPos {
  const jd = date.getTime() / 86400000 + 2440587.5;
  const n = jd - 2451545.0;
  const L0 = (280.46 + 0.9856474 * n) % 360;
  const M = (357.528 + 0.9856003 * n) % 360;
  const e = 0.01671;
  const lambda = (L0 + 1.915 * Math.sin(M * RAD) + 0.02 * Math.sin(2 * M * RAD)) * RAD;
  const epsilon = (23.439 - 0.0000004 * n) * RAD;
  const decl = Math.asin(Math.sin(epsilon) * Math.sin(lambda));
  const y = Math.tan(epsilon / 2) ** 2;
  const eotMin =
    4 *
    (y * Math.sin(2 * L0 * RAD) -
      2 * e * Math.sin(M * RAD) +
      4 * e * y * Math.sin(M * RAD) * Math.cos(2 * L0 * RAD) -
      0.5 * y * y * Math.sin(4 * L0 * RAD) -
      1.25 * e * e * Math.sin(2 * M * RAD));
  const utcHours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
  const solarTime = utcHours + lng / 15 + eotMin / 60;
  const H = (solarTime - 12) * 15 * RAD;
  const sinEl = Math.sin(lat * RAD) * Math.sin(decl) + Math.cos(lat * RAD) * Math.cos(decl) * Math.cos(H);
  const elevation = Math.asin(Math.max(-1, Math.min(1, sinEl))) * DEG;
  let azimuth = 0;
  const cosAz = (Math.sin(decl) - sinEl * Math.sin(lat * RAD)) / (Math.cos(elevation * RAD) * Math.cos(lat * RAD) || 1e-9);
  azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * DEG;
  if (H > 0) azimuth = 360 - azimuth;
  const shadowRatio = elevation > 0 ? 1 / Math.tan(elevation * RAD) : Infinity;
  return {
    elevation,
    azimuth,
    shadowRatio,
    shadowDir: (azimuth + 180) % 360,
    solarTime: ((solarTime % 24) + 24) % 24,
  };
}

function fmtHours(h: number) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

const fmtNum = (v: number, digits = 1) => (Number.isFinite(v) ? v.toFixed(digits) : '—');

/** Tagesverlauf: Sonnenposition alle 30 Minuten. */
function DayTable({ date, lat, lng }: { date: Date; lat: number; lng: number }) {
  const rows = useMemo(() => {
    const out: { t: string; el: number; az: number; ratio: number }[] = [];
    for (let m = 0; m < 24 * 60; m += 30) {
      const d = new Date(date);
      d.setUTCHours(0, m, 0, 0);
      const p = sunPosition(d, lat, lng);
      out.push({ t: fmtHours(p.solarTime), el: p.elevation, az: p.azimuth, ratio: p.shadowRatio });
    }
    return out;
  }, [date, lat, lng]);
  return (
    <div className="day-table">
      <table>
        <thead>
          <tr><th>Solar time</th><th>Elevation</th><th>Azimuth</th><th>Shadow ratio</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={r.el > 0 ? '' : 'day-table__night'}>
              <td className="mono">{r.t}</td>
              <td className="mono">{fmtNum(r.el)}°</td>
              <td className="mono">{fmtNum(r.az)}°</td>
              <td className="mono">{Number.isFinite(r.ratio) ? fmtNum(r.ratio) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Forward() {
  const [date, setDate] = useState('2026-06-21');
  const [time, setTime] = useState('15:50');
  const [lat, setLat] = useState('67');
  const [lng, setLng] = useState('-50');
  const pos = useMemo(() => {
    const d = new Date(`${date}T${time}:00Z`);
    const la = parseFloat(lat);
    const lo = parseFloat(lng);
    if (Number.isNaN(la) || Number.isNaN(lo) || Number.isNaN(d.getTime())) return null;
    return sunPosition(d, la, lo);
  }, [date, time, lat, lng]);
  return (
    <div className="tool-grid">
      <div className="panel">
        <div className="panel__head"><span className="eyebrow">SUN POSITION · FORWARD</span></div>
        <p className="dim">Time → Sun. Enter date/time (UTC), latitude, longitude.</p>
        <div className="sun-form">
          <label className="field"><span className="mono dim">DATE (UTC)</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <label className="field"><span className="mono dim">TIME (UTC)</span><input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
          <label className="field"><span className="mono dim">LATITUDE</span><input value={lat} onChange={(e) => setLat(e.target.value)} /></label>
          <label className="field"><span className="mono dim">LONGITUDE</span><input value={lng} onChange={(e) => setLng(e.target.value)} /></label>
        </div>
        {pos && (
          <div className="sun-result">
            <div className="sun-result__big">
              <span className="eyebrow">ELEVATION</span>
              <b>{fmtNum(pos.elevation)}°</b>
            </div>
            <dl className="kv">
              <div><dt>Azimuth</dt><dd>{fmtNum(pos.azimuth)}°</dd></div>
              <div><dt>Shadow ratio</dt><dd>{Number.isFinite(pos.shadowRatio) ? fmtNum(pos.shadowRatio) : '—'}</dd></div>
              <div><dt>Shadow direction</dt><dd>{fmtNum(pos.shadowDir)}°</dd></div>
              <div><dt>Local solar time</dt><dd>{fmtHours(pos.solarTime)}</dd></div>
            </dl>
          </div>
        )}
      </div>
      <div className="panel">
        <div className="panel__head"><span className="eyebrow">DAY PROFILE</span></div>
        <p className="dim">Sun path over the whole day (local solar time). Night rows are dimmed.</p>
        {pos && <DayTable date={new Date(`${date}T00:00:00Z`)} lat={parseFloat(lat)} lng={parseFloat(lng)} />}
      </div>
    </div>
  );
}

function Reverse() {
  const [date, setDate] = useState('2026-06-21');
  const [lat, setLat] = useState('67');
  const [lng, setLng] = useState('-50');
  const [shadowDir, setShadowDir] = useState('70');
  const [dirTol, setDirTol] = useState('8');
  const [mode, setMode] = useState<'ratio' | 'elevation'>('ratio');
  const [shadowRatio, setShadowRatio] = useState('1.5');
  const [elevation, setElevation] = useState('34');

  const windows = useMemo(() => {
    const la = parseFloat(lat);
    const lo = parseFloat(lng);
    const sd = parseFloat(shadowDir);
    const tol = parseFloat(dirTol);
    const target = mode === 'ratio' ? parseFloat(shadowRatio) : parseFloat(elevation);
    if ([la, lo, sd, tol, target].some(Number.isNaN)) return [];
    const hits: { t: number; el: number; az: number }[] = [];
    const base = new Date(`${date}T00:00:00Z`);
    for (let m = 0; m < 24 * 60; m += 5) {
      const d = new Date(base);
      d.setUTCMinutes(m);
      const p = sunPosition(d, la, lo);
      if (p.elevation <= 0) continue;
      const dirMatch = Math.abs(((p.shadowDir - sd + 180) % 360) - 180) <= tol;
      const valMatch = mode === 'ratio'
        ? Math.abs(p.shadowRatio - target) / target <= 0.05
        : Math.abs(p.elevation - target) <= 2;
      if (dirMatch && valMatch) hits.push({ t: p.solarTime, el: p.elevation, az: p.azimuth });
    }
    // group consecutive hits (5-min steps) into windows
    const windowsOut: { from: number; to: number; mid: number }[] = [];
    for (const h of hits) {
      const last = windowsOut[windowsOut.length - 1];
      if (last && h.t - last.to <= 0.1) last.to = h.t;
      else windowsOut.push({ from: h.t, to: h.t, mid: h.t });
    }
    return windowsOut;
  }, [date, lat, lng, shadowDir, dirTol, mode, shadowRatio, elevation]);

  return (
    <div className="tool-grid">
      <div className="panel">
        <div className="panel__head"><span className="eyebrow">CHRONO-LOCATE · REVERSE</span></div>
        <p className="dim">Shadow → time. Enter shadow direction and either shadow ratio or elevation; get matching time windows (local solar time).</p>
        <div className="sun-form">
          <label className="field"><span className="mono dim">DATE</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <label className="field"><span className="mono dim">LATITUDE</span><input value={lat} onChange={(e) => setLat(e.target.value)} /></label>
          <label className="field"><span className="mono dim">LONGITUDE</span><input value={lng} onChange={(e) => setLng(e.target.value)} /></label>
          <label className="field"><span className="mono dim">SHADOW DIRECTION °</span><input value={shadowDir} onChange={(e) => setShadowDir(e.target.value)} /></label>
          <label className="field"><span className="mono dim">DIRECTION TOLERANCE °</span><input value={dirTol} onChange={(e) => setDirTol(e.target.value)} /></label>
        </div>
        <div className="row">
          <button className={`chip${mode === 'ratio' ? ' chip--on' : ''}`} onClick={() => setMode('ratio')}>By shadow ratio</button>
          <button className={`chip${mode === 'elevation' ? ' chip--on' : ''}`} onClick={() => setMode('elevation')}>By elevation</button>
        </div>
        {mode === 'ratio' ? (
          <label className="field"><span className="mono dim">SHADOW RATIO (L/H)</span><input value={shadowRatio} onChange={(e) => setShadowRatio(e.target.value)} /></label>
        ) : (
          <label className="field"><span className="mono dim">ELEVATION °</span><input value={elevation} onChange={(e) => setElevation(e.target.value)} /></label>
        )}
        <div className="sun-result">
          <span className="eyebrow">MATCHING WINDOWS (LOCAL SOLAR TIME)</span>
          {windows.length === 0 && <p className="dim">No match — loosen tolerance or check inputs.</p>}
          {windows.map((w, i) => (
            <div key={i} className="win-row">
              <span className="event-type event-type--green">{fmtHours(w.from)} – {fmtHours(w.to)}</span>
              <span className="mono dim">mid {fmtHours(w.mid)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="panel">
        <div className="panel__head"><span className="eyebrow">HOW TO MEASURE</span></div>
        <ul className="tips">
          <li><b>Direction:</b> North = 0°, East = 90°. A shadow pointing northwest ≈ 315°.</li>
          <li><b>Ratio:</b> shadow length ÷ object height. Ratio 1.5 ⇒ elevation ≈ arctan(1/1.5) ≈ 34°.</li>
          <li><b>Time zones:</b> results are <b>local solar time</b>. Add/subtract your longitude correction and the equation of time to get clock time.</li>
          <li>Check both morning and evening — shadows are ambiguous. Use direction to disambiguate.</li>
          <li>Cross-check with a second object (different height, same shadow direction).</li>
        </ul>
      </div>
    </div>
  );
}

export function SunShadowTool() {
  return (
    <div className="sun-tool">
      <Forward />
      <Reverse />
    </div>
  );
}
